"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-9 w-20 animate-pulse rounded-md bg-muted" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={cn(navigationMenuTriggerStyle(), "bg-transparent text-foreground hover:bg-transparent focus:bg-transparent data-[active]:bg-transparent data-[state=open]:bg-transparent", "relative font-default text-sm font-bold uppercase tracking-widest after:absolute after:bottom-1 after:left-4 after:right-4 after:h-[2px] after:origin-bottom-right after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100")}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-2 py-4">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-10 w-full animate-pulse rounded-md bg-muted" />)}
      </div>;
  }
  return <nav className="flex flex-col space-y-1 py-4">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="block rounded-md px-4 py-3 text-base font-default font-bold uppercase tracking-wider text-foreground hover:bg-accent hover:text-accent-foreground transition-colors" onClick={onItemClick}>
            {item.label}
          </Link>
        </div>)}
      <div className="px-4 pt-4 pb-2">
        <Button asChild className="w-full bg-primary text-primary-foreground font-default font-bold uppercase tracking-widest rounded-md" size="lg">
          <Link to="#" onClick={onItemClick}>
            Book Assessment
          </Link>
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="265048" id="header" className={cn("sticky top-0 z-50 w-full border-b transition-all duration-300", isScrolled ? "border-border bg-background/80 backdrop-blur-md shadow-md" : "border-border/40 bg-background")}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex h-20 items-center justify-between">
          {}
          <div className="flex-shrink-0">
            <Link to="/HomePage" className="flex items-center gap-2 transition-opacity hover:opacity-90">
              <WvcLogo className="h-12 w-auto text-foreground" style={{
              maxHeight: "60px"
            }} />
            </Link>
          </div>

          {}
          <div className="hidden flex-1 items-center justify-center md:flex">
            <MenuProvider menu_id="16">
              <HeaderMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="hidden flex-shrink-0 md:flex">
            <Button asChild size="default" className="bg-primary text-primary-foreground font-default font-bold uppercase tracking-widest rounded-md shadow-sm hover:bg-primary/90 transition-all">
              <a href="#start-your-recovery">Book Assessment</a>
            </Button>
          </div>

          {}
          <div className="flex items-center md:hidden">
            <Button variant="ghost" size="icon" className="text-foreground" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="border-t border-border bg-background md:hidden">
          <div className="container mx-auto px-4">
            <MenuProvider menu_id="16">
              <MobileMenuConsumer onItemClick={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>
        </div>}
    </section>;
}